import { To, useNavigate } from 'react-router-dom';

import { useLazyGetAppointmentQuery } from 'services/appointments/appointments';
import { useLazyGetEntitlementsQuery } from 'services/marketone/marketone';
import {
  isCancelledEntitlementStatus,
  isEntitlementUncancelled
} from 'services/marketone/marketone.types';
import { useLazyGetSubscriptionInfoQuery } from 'services/myAccount/myAccount';
import { useLazyGetMyProgramsQuery, useLazyGetProgramsQuery } from 'services/programs/programs';
import { useLazyGetSurveysQuery } from 'services/surveys/surveys';
import { useLazyGetArticleByIdQuery } from 'services/wm/wm';

import { selectUser } from 'store';
import { setModal } from 'store/modals/modalSlice';

import {
  MH_ONBOARDING_FLOW,
  WH_ONBOARDING_FLOW,
  WINBACK_SD_FLOW,
  WM_ONBOARDING_FLOW
} from 'constants/externalLinks';
import { PROGRAM_CONFIGS } from 'constants/programs';
import { useAppDispatch, useAppSelector, useQuery } from 'hooks';
import { hasEligibilityAccess, hasPaymentMethodOnFile } from 'hooks/usePaywall/eligibilityAccess';
import { usePaywall } from 'hooks/usePaywall/usePaywall';
import { useSelectFeatureStatus } from 'hooks/useSelectFeatureStatus';
import { useStartRenewal } from 'hooks/useStartRenewal/useStartRenewal';
import { DeepLinkAction, MessageTab } from 'utils/enums';
import { PathName } from 'router/paths';
import { session, SESSION_KEYS } from 'utils/storage';

import { ProgramStatus, UserProgram } from 'models/programs.types';
import { ActionType } from 'models/survey.types';
import { FeatureStatusType } from 'models/user.types';

import {
  getPausedProgramIds,
  handleCreateAppt,
  handleDeepLinkAppointmentReschedule,
  handleDeepLinkNoShowFeeCharged,
  handleDeepLinkResourcesPage,
  handleDeepLinkSurvey,
  resolveProgramByQuery
} from './useDeepLinks.settings';

/**
 * Exposes helpers that resolve internal navigation targets for supported deep-link query actions.
 */
export const useDeepLinks = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const query = useQuery();

  const { accessToken, paymentProfiles, refreshToken, useNewPostOnboarding } =
    useAppSelector(selectUser);
  const isStoreDisabled = useSelectFeatureStatus('store');

  const [getArticleById] = useLazyGetArticleByIdQuery();
  const [getAppointment] = useLazyGetAppointmentQuery();
  const [getAllPrograms] = useLazyGetProgramsQuery();
  const [getSubscriptionInfo] = useLazyGetSubscriptionInfoQuery();
  const [getEntitlements] = useLazyGetEntitlementsQuery();
  const [getMyPrograms] = useLazyGetMyProgramsQuery();
  const [getSurveys] = useLazyGetSurveysQuery();
  const { checkEligibility, handlePaywallError } = usePaywall();
  const { startRenewal } = useStartRenewal();
  // Lets the survey resolver fall back to a fetch when the cached list cannot answer the link.
  const fetchSurveys = () => getSurveys().unwrap();

  const handleDeepLinkProgramOnboarding = async ({
    activeQuery,
    isAuth,
    userPrograms
  }: {
    activeQuery: URLSearchParams;
    isAuth: boolean;
    userPrograms?: UserProgram[];
  }): Promise<To | null> => {
    const requireLogin = activeQuery.get('requireLogin') === 'true';
    const programCode = activeQuery.get('programCode');
    const program = activeQuery.get('program');
    const programCategory = activeQuery.get('programCategory');
    const programFromQuery = program || programCode || programCategory || '';

    try {
      if (requireLogin && !isAuth) {
        return null;
      }
      if (!isAuth) {
        if (programFromQuery.includes('mental-health')) {
          const url = new URL(MH_ONBOARDING_FLOW);
          url.searchParams.set('provider', 'vpc');
          window.open(url.toString(), '_self');
          return null;
        }
        if (programFromQuery.includes('women-health')) {
          const url = new URL(WH_ONBOARDING_FLOW);
          url.searchParams.set('provider', 'vpc');
          window.open(url.toString(), '_self');
          return null;
        }
        if (programFromQuery.includes('weight-management') || programFromQuery.includes('wm')) {
          const url = new URL(WM_ONBOARDING_FLOW);
          url.searchParams.set('provider', 'vpc');
          window.open(url.toString(), '_self');
          return null;
        }
      }
      const isMobileView = session.get(SESSION_KEYS.MOBILE_WEBVIEW) === 'true';
      if (isMobileView) {
        switch (programCategory) {
          case 'weight-management':
            const wasWeightManagement = userPrograms?.some(
              (p) => p.program.category === 'weight-management'
            );
            if (wasWeightManagement) {
              const url = new URL(WINBACK_SD_FLOW);
              url.searchParams.set('provider', 'vpc');
              if (accessToken) url.searchParams.set('accessToken', accessToken);
              if (refreshToken) url.searchParams.set('refreshToken', refreshToken);
              window.open(url.toString(), '_self');
              return null;
            } else {
              return {
                pathname: PathName.MigrateToWM
              };
            }
          case 'mental-health':
            return {
              pathname: PathName.Join,
              search: 'f=mental-health&s=intro-1'
            };
          case 'women-health':
            const url = new URL(WH_ONBOARDING_FLOW);
            url.searchParams.set('provider', 'vpc');
            if (accessToken) url.searchParams.set('accessToken', accessToken);
            if (refreshToken) url.searchParams.set('refreshToken', refreshToken);
            window.open(url.toString(), '_self');
            return null;
          default:
            break;
        }
      }
      const { data: allPrograms } = await getAllPrograms().unwrap();
      const programData = resolveProgramByQuery(programFromQuery, allPrograms);
      if (!programData) {
        return {
          pathname: PathName.Home
        };
      }
      const arg =
        programData.type === 'category'
          ? {
              programCategory: programData.program.category
            }
          : { programCode: programData.program.code };
      const status = await checkEligibility({
        ...arg,
        type: 'onboarding'
      });
      const hasAccess = hasEligibilityAccess(status, hasPaymentMethodOnFile(paymentProfiles));
      if (!hasAccess) {
        handlePaywallError(status, { ctx: 'onboarding' });
        return null;
      }
      return (await handleDeepLinkSurvey(activeQuery, fetchSurveys))?.target ?? null;
    } catch {
      return null;
    }
  };

  const handleSurvey = async (activeQuery: URLSearchParams, activeUserPrograms?: UserProgram[]) => {
    const orderId = activeQuery.get('orderId');
    const type = activeQuery.get('type') || 'renewal';
    try {
      const resolution = await handleDeepLinkSurvey(activeQuery, fetchSurveys, activeUserPrograms);
      // Nothing open to navigate to (completed, canceled or unknown survey).
      if (!resolution) return null;

      // Keyed by the resolved survey, whose own type picks the endpoint path — the link's `type`
      // is absent on most surveyId links and would gate them as renewals. Device-family links
      // keep their orderId-keyed check, since they route on the query params.
      const status = resolution.survey
        ? await checkEligibility({
            surveyId: resolution.survey._id,
            surveyType: resolution.survey.type,
            type: 'survey'
          })
        : orderId
          ? await checkEligibility({ orderId, surveyType: type as ActionType, type: 'survey' })
          : null;
      if (status) {
        const hasAccess = hasEligibilityAccess(status, hasPaymentMethodOnFile(paymentProfiles));
        if (!hasAccess) {
          handlePaywallError(status, { ctx: 'survey' });
          return null;
        }
      }
      return resolution.target;
    } catch {
      return null;
    }
  };

  /**
   * Resolves an `action=medication-renewal` link to a renewal-start argument, then defers to the shared
   * `useStartRenewal` so this entry point reads the response exactly like My Prescriptions and Get care.
   */
  const handleMedicationRenewal = async (
    elationPrescriptionId: string | null,
    programFromQuery: string,
    userPrograms?: UserProgram[]
  ): Promise<To | null> => {
    // Prefer the finer per-prescription id, but only when it's a usable positive integer — a
    // non-numeric value would serialize as `null`, and blank/zero-ish input (e.g. `%20` → 0) must
    // not shadow the usable program fallback.
    const parsedPrescriptionId = elationPrescriptionId ? Number(elationPrescriptionId) : NaN;
    if (Number.isInteger(parsedPrescriptionId) && parsedPrescriptionId > 0) {
      return await startRenewal({ elationPrescriptionId: parsedPrescriptionId });
    }

    const resolvedProgram = resolveProgramByQuery(programFromQuery, userPrograms);
    if (!resolvedProgram) return null;
    return await startRenewal({ programId: resolvedProgram.program.program._id });
  };

  const handleDeepLinks = async (
    userPrograms?: UserProgram[],
    isAuth = false,
    storeStatus?: FeatureStatusType,
    overrideQuery?: URLSearchParams
  ): Promise<To | null> => {
    const activeQuery = overrideQuery ?? query;
    const action = activeQuery.get('action');
    if (!action) return null;

    const chatId = activeQuery.get('chatId');
    const appointmentId = activeQuery.get('appointmentId');
    const invoiceId = activeQuery.get('invoiceId');
    const programCode = activeQuery.get('programCode');
    const program = activeQuery.get('program');
    const programCategory = activeQuery.get('programCategory');
    const elationPrescriptionId = activeQuery.get('elationPrescriptionId');
    const pageId = activeQuery.get('pageId');
    const productId = activeQuery.get('productId');
    const programFromQuery = program || programCode || programCategory || '';

    const isStoreDisabledState = storeStatus ? storeStatus === 'disabled' : isStoreDisabled;
    // Callers pass every program (active + inactive). Targets behind `RequireProgram` / the identity
    // banner only recognise active ones, so resolve those against this list.
    const activeUserPrograms = (userPrograms ?? []).filter(
      (userProgram) => userProgram?.program?.status === ProgramStatus.Active
    );

    switch (action) {
      case DeepLinkAction.AppointmentDetails:
        if (!appointmentId) return null;
        return {
          pathname: `${PathName.Appointment}/${appointmentId}`
        };
      case DeepLinkAction.AppointmentReschedule:
        if (!appointmentId) return null;
        return handleDeepLinkAppointmentReschedule(appointmentId, getAppointment);
      case DeepLinkAction.ProgramOnboarding:
        return await handleDeepLinkProgramOnboarding({ activeQuery, isAuth, userPrograms });
      case DeepLinkAction.Dashboard:
        return { pathname: PathName.Home };
      case DeepLinkAction.DiscountCard:
        return { pathname: PathName.DiscountCard };
      case DeepLinkAction.CreateAppointment:
        return handleCreateAppt(activeQuery);
      case DeepLinkAction.SemaglutideMigration:
        return { pathname: PathName.MoveFromSemaglutide };
      case DeepLinkAction.ResumeProgramPaywall:
        const data = await getMyPrograms({
          expand: ['subscription']
        }).unwrap();
        const programIds = getPausedProgramIds(data);
        if (!programIds.length) return null;
        dispatch(
          setModal({
            key: 'resumeToProgram',
            value: {
              programIds,
              show: true
            }
          })
        );
        return null;
      case DeepLinkAction.AttCancel:
      case DeepLinkAction.AttRemoveBenefit: {
        // Both sheets are for AT&T patients only. `isPartnerBenefitActive` is the server's answer and
        // is never re-derived here; without it the patient simply lands on billing, no error shown.
        //
        // Bail before the request when unauthenticated: `/marketone/entitlements` needs a session, and
        // a 401 goes through `baseQueryWithReauth`, which toasts "Session has expired", clears the user
        // and hard-redirects to /login — dropping `?action=` so the link is never replayed after sign-in.
        if (!isAuth) return null;

        // `error` is checked separately from an inactive benefit: `marketoneApi` is in
        // `SILENT_ERROR_REDUCER_PATHS` (store/index.ts), a suppression meant for background polling on
        // the billing page — not for a user following a cancellation email. Without this, a 500 or a
        // timeout would look exactly like "not an AT&T patient" and leave no trace anywhere.
        const { data: entitlements, error } = await getEntitlements();
        if (error) {
          // eslint-disable-next-line no-console -- the global error toast is suppressed for this slice
          console.error('marketone/entitlements failed resolving an AT&T deep link', error);
          return { pathname: PathName.BillingDetails };
        }

        if (!entitlements?.data.isPartnerBenefitActive) {
          return { pathname: PathName.BillingDetails };
        }

        // The remove-benefit sheet is about a price change that has not happened yet, so it is not
        // offered once the benefit is already running out — `isPartnerBenefitActive` stays true through
        // that final cycle. The card's button hides on the same condition.
        const rows = entitlements.data.entitlements ?? [];
        const isEnding =
          !rows.some(isEntitlementUncancelled) &&
          rows.some(
            ({ status, subscriptionEndDate }) =>
              isCancelledEntitlementStatus(status) && !!subscriptionEndDate
          );
        if (action === DeepLinkAction.AttRemoveBenefit && isEnding) {
          return { pathname: PathName.BillingDetails };
        }

        // Both sheets read subscription fields through `useMyPrograms` — the scheduled cancellation
        // date, paused state, prices. The bootstrap `getMyPrograms()` expands `details` only, so
        // without this the sheets open against subscription-less rows: a patient who already has a
        // cancellation scheduled reads as freshly cancellable and gets pitched "You're already on our
        // best price" until the billing page's own query lands. Same reason `ResumeProgramPaywall`
        // above expands `subscription` before dispatching.
        // `details` is repeated deliberately: `getMyPrograms` spreads params over its defaults, so
        // passing `expand` replaces `['details']` rather than adding to it — and `setPrograms`
        // replaces whole rows, so a subscription-only fetch strips `details` from every program
        // app-wide until the billing page's own query lands. Matches `useCreateAppointment`.
        await getMyPrograms({ expand: ['subscription', 'details'] }).unwrap();

        dispatch(
          setModal({
            key: action === DeepLinkAction.AttCancel ? 'attCancellation' : 'attRemoveBenefit',
            value: { show: true }
          })
        );
        return { pathname: PathName.BillingDetails };
      }
      case DeepLinkAction.BillingPastDuePopup:
        const { data: subscriptionInfo, error } = await getSubscriptionInfo();
        if (error || !subscriptionInfo) return null;
        if (subscriptionInfo.data.hasUnpaidInvoice) {
          dispatch(
            setModal({
              key: 'paymentPastDue',
              value: {
                show: true
              }
            })
          );
        }
        return null;
      case DeepLinkAction.PostOnboardingNew:
        return { pathname: PathName.PostOnboarding };
      case DeepLinkAction.TirzepatideMigration:
        return { pathname: PathName.MoveFromTirzepatide };
      case DeepLinkAction.FrontDeskChannel:
        return {
          pathname: PathName.Messages,
          search: `tab=${MessageTab.FrontDesk}&chatId=${chatId}`
        };
      case DeepLinkAction.IntakeForm:
        return {
          pathname:
            programFromQuery === 'women-health' ? PathName.PostOnboardingWH : PathName.IntakeForm
        };
      case DeepLinkAction.Labs:
        return { pathname: PathName.Labs };
      case DeepLinkAction.Shop: {
        if (isStoreDisabledState || !userPrograms?.length) return null;
        return { pathname: PathName.Shop };
      }
      case DeepLinkAction.StoreProduct: {
        if (isStoreDisabledState || !userPrograms?.length || !productId) return null;
        return { pathname: `${PathName.ShopProducts}/${productId}` };
      }
      case DeepLinkAction.MessageChannel:
        return {
          pathname: PathName.Messages,
          search: `tab=${MessageTab.CareTeam}&chatId=${chatId}`
        };
      case DeepLinkAction.PlanRecommendation:
        return { pathname: PathName.MigrateToPlan };
      case DeepLinkAction.UnpaidInvoice:
        return {
          pathname: PathName.UnpaidInvoice,
          search: `?invoiceId=${invoiceId}`
        };
      case DeepLinkAction.PartnerBenefitUnlinked:
        return { pathname: PathName.PartnerBenefit };
      case DeepLinkAction.UpdateCreditCard:
        return { pathname: PathName.BillingDetails };
      case DeepLinkAction.AppointmentRequired:
        return { pathname: PathName.AppointmentRequired };
      case DeepLinkAction.WMWinback: {
        if (!isAuth) {
          return null;
        }
        const activePrograms = userPrograms?.filter(
          (p) => p.program.status === ProgramStatus.Active
        );
        const hadOrHasWeightManagement = userPrograms?.some(
          (p) => p.program.category === 'weight-management'
        );

        const activeWeightManagement = activePrograms?.find(
          (p) => p.program.category === 'weight-management'
        );

        const wasWeightManagement = !activeWeightManagement && hadOrHasWeightManagement;

        if (wasWeightManagement) {
          const url = new URL(WINBACK_SD_FLOW);
          url.searchParams.set('provider', 'vpc');
          if (accessToken) url.searchParams.set('accessToken', accessToken);
          if (refreshToken) url.searchParams.set('refreshToken', refreshToken);
          window.open(url.toString(), '_self');
        }
        return null;
      }
      case DeepLinkAction.ResourcesPage:
        return await handleDeepLinkResourcesPage(
          getArticleById,
          pageId,
          programFromQuery,
          userPrograms
        );
      case DeepLinkAction.MedicationRenewal:
        return await handleMedicationRenewal(elationPrescriptionId, programFromQuery, userPrograms);
      case DeepLinkAction.Survey:
        return await handleSurvey(activeQuery, activeUserPrograms);
      case DeepLinkAction.WaitingRoom:
        return {
          pathname: PathName.ZoomCall,
          search: `callId=${appointmentId}`
        };
      case DeepLinkAction.NovocareAgreementRequired:
        return { pathname: PathName.MedicationConfirmation };
      case DeepLinkAction.AgreementRequired:
        const documentType = activeQuery.get('documentType') || '';
        return {
          pathname: PathName.MedicationConfirmation,
          search: documentType ? `documentType=${documentType}` : ''
        };
      case DeepLinkAction.NewSupportRequest:
        const requestType = activeQuery.get('requestType') || '';
        const message = activeQuery.get('message') || '';

        const params = new URLSearchParams();

        params.append('create-new-request', 'true');
        if (requestType) {
          params.append('requestType', requestType);
        }
        if (message) {
          params.append('message', message);
        }
        return {
          pathname: PathName.Messages,
          search: `?${params.toString()}`
        };
      case DeepLinkAction.NoShowFeeCharged:
        if (!appointmentId) return null;
        return handleDeepLinkNoShowFeeCharged(appointmentId, getAppointment, navigate);
      case DeepLinkAction.VerifyIdentity: {
        // Authenticated surface — defer while logged out (resolved on the post-login pass).
        if (!isAuth) return null;
        // Mirror IdentityVerificationBanner's target: WH → WH post-onboarding; MH → Men's intake; else
        // intake. Match **active** programs only, exactly as the banner and `RequireProgram` do — an
        // inactive WH/MH program would otherwise be bounced off those guarded routes.
        const isWomensHealth = activeUserPrograms.some(
          (up) => up.program.category === 'women-health'
        );
        if (isWomensHealth) return { pathname: PathName.PostOnboardingWH };
        const isMensHealth = activeUserPrograms.some((up) => up.program.category === 'mens-health');
        if (isMensHealth) {
          return { pathname: PathName.IntakeFormMensTRT, search: 's=verify-identity' };
        }
        /*
         * A `useNewPostOnboarding` WM patient's flow (`PostOnboardingAlt`) has no `verify-identity`
         * step — Verify ID is a modal on its Intro checklist, not a step this query string can name
         * — so it re-derives its own current step from the patient's actual state (medication first,
         * per LMD-1401) rather than trusting this link's intent either way. Route there directly
         * instead of through the legacy `IntakeForm` guard: that guard's redirect for these patients
         * drops this query string today, so the two paths already land in the same place — this just
         * makes it explicit rather than an accident of how that guard happens to redirect.
         */
        if (useNewPostOnboarding) return { pathname: PathName.PostOnboarding };
        return { pathname: PathName.IntakeForm, search: 's=verify-identity' };
      }
      case DeepLinkAction.SelectMedication: {
        // Authenticated surface — defer while logged out (resolved on the post-login pass).
        if (!isAuth) return null;
        /*
         * Weight-management only by construction — the backend's `SELECT_MEDICATION` blocker (LMD-1401)
         * is never emitted for any other program category. `PostOnboardingAlt` already routes a
         * `useNewPostOnboarding` patient to `BenefitsCheck` first whenever `needToSetPreferredMedication`
         * is true, so it self-derives with no query param needed — same reasoning as `VerifyIdentity`'s
         * WM branch above. The legacy flow's equivalent step is named `preferred-medication`.
         */
        return useNewPostOnboarding
          ? { pathname: PathName.PostOnboarding }
          : { pathname: PathName.IntakeForm, search: 's=preferred-medication' };
      }
      case DeepLinkAction.Order: {
        if (!isAuth) return null;
        // The shop-order route is nested under the store-flag guard, so honour the same gate the
        // `Shop` / `StoreProduct` cases use rather than landing on the guard's redirect.
        if (isStoreDisabledState) return null;
        const orderId = activeQuery.get('orderId');
        if (!orderId) return null;
        // Encoded: an id carrying `&`, `=`, `/` or `#` would otherwise corrupt the URL. The id itself is
        // not validated here — an unknown one lands on the order page's own empty state.
        return { pathname: `${PathName.ShopOrders}/${encodeURIComponent(orderId)}` };
      }
      case DeepLinkAction.WMDevicesShipment: {
        if (!isAuth) return null;
        // Mirrors mobile, which switches to the Weight tab and lets the Weight controller fetch the device
        // order by id before opening its tracking popup. Keep the action + orderId in the search so
        // `DeviceShipmentDeepLink` on that page can do the same, wherever the link was opened from.
        const deviceOrderId = activeQuery.get('orderId');
        if (!deviceOrderId) return null;
        // Built through URLSearchParams so an id containing `&`/`=` cannot inject extra params.
        const deviceSearch = new URLSearchParams({
          action: DeepLinkAction.WMDevicesShipment,
          orderId: deviceOrderId
        });
        return { pathname: PathName.Weight, search: deviceSearch.toString() };
      }
      case DeepLinkAction.Program: {
        if (!isAuth) return null;
        // Match against the patient's **active** programs by id (primary) or code/category (aliases), then
        // route to that program's category page. Inactive programs are excluded because the program routes
        // sit behind `RequireProgram`, which redirects to NotFound when the program is not active.
        const resolvedProgram = resolveProgramByQuery(
          activeQuery.get('programId') || programFromQuery,
          activeUserPrograms
        );
        const programPath = PROGRAM_CONFIGS.find(
          ({ category }) => category === resolvedProgram?.program.program.category
        )?.path;
        return programPath ? { pathname: programPath } : null;
      }
      default:
        return null;
    }
  };

  return {
    handleDeepLinks
  };
};
